<template>
  <div>
    <v-container fluid>
      <Govem_bar />
      <base-material-card
        icon="mdi-calculator"
        title="คำนวณค่าตอบแทนพนักงานราชการ"
        class="px-5 py-3"
      >
        <!-- ส่วนการตั้งค่าการประเมิน -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert
              type="warning"
              prominent
              outlined
              border="left"
              colored-border
              elevation="2"
              icon="mdi-alert-circle"
            >
              <div class="text-subtitle-1 font-weight-medium">
                ⚠️ ระบบคำนวณค่าตอบแทนพนักงานราชการ
              </div>
              <div class="text-body-2">
                ✓ ตรวจสอบแล้วตามหลักเกณฑ์ประกาศคณะกรรมการบริหารพนักงานราชการ
              </div>
              <div class="text-body-2 mt-2">
                📋 ขีดจำกัด: ไม่เกิน 4% ของเงินเดือนรวม | ปัดเศษขึ้นเป็นหลักสิบ
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- ส่วนการตั้งค่าพื้นฐาน -->
        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="ปีงบประมาณ"
              outlined
              dense
              @change="loadEvaluationData"
              hint="เลือกปีที่ต้องการคำนวณค่าตอบแทน"
              persistent-hint
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="compensationPercentage"
              label="เปอร์เซ็นต์การเลื่อน (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              max="4"
              suffix="%"
              readonly
              @input="recalculateBudget"
              hint="ค่าเริ่มต้น: 4% (ไม่เกิน 4%)"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="maxAllowedPercentage"
              label="สูงสุดรายบุคคล (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              max="6"
              suffix="%"
              readonly
              @input="recalculateBudget"
              hint="ค่าเริ่มต้น: 6% ต่อคน"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="roundingMethod"
              :items="roundingMethods"
              label="วิธีปัดเศษ"
              outlined
              dense
              readonly
              hint="เลือกวิธีปัดเศษขึ้น"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>

        <!-- เครื่องมือจัดการ Columns -->
        <v-row class="mb-3">
          <v-col cols="12">
            <v-card outlined class="pa-3 mb-2">
              <v-row align="center">
                <v-col cols="12" md="8">
                  <div class="text-subtitle-2 mb-2">📋 จัดการการแสดงผล</div>
                  <v-chip-group multiple column>
                    <v-chip
                      v-for="header in selectedHeaders"
                      :key="header.value"
                      close
                      small
                      color="primary"
                      text-color="white"
                      @click:close="removeHeader(header)"
                    >
                      {{ header.text }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    small
                    color="success"
                    text
                    @click="selectAllHeaders"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-check-all</v-icon>
                    ทั้งหมด
                  </v-btn>
                  <v-btn
                    small
                    color="info"
                    text
                    @click="selectEssentialHeaders"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-star</v-icon>
                    สำคัญ
                  </v-btn>
                  <v-btn
                    small
                    color="warning"
                    text
                    @click="resetHeaders"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    รีเซ็ต
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- สรุปวงเงินรวม -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card class="pa-4" outlined>
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <v-card-text class="text-center">
                    <div class="display-1 blue--text font-weight-bold">
                      {{ totalPersonnel }}
                    </div>
                    <p class="subtitle-2">จำนวนพนักงาน</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card-text class="text-center">
                    <div class="display-1 green--text font-weight-bold">
                      {{ formatCurrency(totalSalary) }}
                    </div>
                    <p class="subtitle-2">เงินเดือนรวม</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card-text class="text-center">
                    <div class="display-1 orange--text font-weight-bold">
                      {{ formatCurrency(totalCompensationBudget) }}
                    </div>
                    <p class="subtitle-2">วงเงินค่าตอบแทน</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card-text class="text-center">
                    <div
                      class="display-1 font-weight-bold"
                      :class="budgetStatus.class"
                    >
                      {{ formatCurrency(totalCompensationDistributed) }}
                    </div>
                    <p class="subtitle-2">จัดสรรแล้ว</p>
                    <v-chip
                      x-small
                      :color="budgetStatus.color"
                      text-color="white"
                    >
                      {{ budgetStatus.text }}
                    </v-chip>
                  </v-card-text>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <v-row>
                <v-col cols="12" md="4">
                  <v-progress-linear
                    :value="budgetPercentage"
                    height="25"
                    rounded
                    color="success"
                  >
                    <span class="white--text font-weight-bold">
                      {{ budgetPercentage.toFixed(1) }}%
                    </span>
                  </v-progress-linear>
                  <p class="caption mt-1">การใช้วงเงิน</p>
                </v-col>
                <v-col cols="12" md="4">
                  <div>
                    <strong>เงินคงเหลือ:</strong>
                    <span class="text-right" :class="remainingBudgetColor">
                      {{ formatCurrency(remainingBudget) }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div>
                    <strong>เกินวงเงิน:</strong>
                    <span class="text-right" :class="excessBudgetColor">
                      {{ formatCurrency(Math.max(0, totalCompensationDistributed - totalCompensationBudget)) }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- ตารางผลการคำนวณ -->
        <v-card class="mb-4" outlined>
          <v-card-title>
            <div class="d-flex justify-space-between align-center w-100">
              <span>📊 ตารางการคำนวณค่าตอบแทน</span>
              <v-chip
                :color="calculationComplete ? 'success' : 'warning'"
                text-color="white"
                small
              >
                {{ calculationComplete ? "✓ คำนวณแล้ว" : "⏳ รอคำนวณ" }}
              </v-chip>
            </div>
          </v-card-title>

          <v-data-table
            :headers="compensationHeaders"
            :items="compensationData"
            :loading="loading"
            class="elevation-0"
            hide-default-footer
            disable-pagination
            fixed-header
            height="600px"
            dense
          >
            <!-- คะแนนการประเมิน - สามารถแก้ไขได้ -->
            <template v-slot:item.performance_score="{ item }">
              <v-text-field
                v-model.number="item.performance_score"
                type="number"
                step="0.01"
                min="0"
                max="100"
                dense
                outlined
                @input="validateScore(item)"
                @blur="calculateCompensation"
                :disabled="calculationComplete"
                hide-details
                class="score-input"
              ></v-text-field>
            </template>

            <!-- เงินเดือน -->
            <template v-slot:item.salary_s="{ item }">
              <span class="font-weight-bold text-right">
                {{ formatCurrency(item.salary_s) }}
              </span>
            </template>

            <!-- สูงสุดรายบุคคล 6% -->
            <template v-slot:item.max_increase_6_percent="{ item }">
              <span class="font-weight-bold purple--text text-right">
                {{ formatCurrency(item.max_increase_6_percent) }}
              </span>
            </template>

            <!-- ร้อยละที่ได้เลื่อนรายบุคคล (ยังไม่บริหารวงเงิน) -->
            <template v-slot:item.individual_percentage_pre_manage="{ item }">
              <span :class="getPercentageColor(item.individual_percentage_pre_manage)">
                {{ formatPercentage3Decimal(item.individual_percentage_pre_manage) }}%
              </span>
            </template>

            <!-- เงินเลื่อนขั้นต้น (ทศนิยม 2) -->
            <template v-slot:item.initial_increase_pre_manage="{ item }">
              <span class="font-weight-bold teal--text text-right">
                {{ formatCurrency2Decimal(item.initial_increase_pre_manage) }}
              </span>
            </template>

            <!-- ปัดเศษสิบขั้นต้น -->
            <template v-slot:item.initial_rounded_pre_manage="{ item }">
              <span class="font-weight-bold indigo--text text-right">
                {{ formatCurrency(item.initial_rounded_pre_manage) }}
              </span>
            </template>

            <!-- ร้อยละการบริหารวงเงิน -->
            <template v-slot:item.management_percentage="{ item }">
              <span class="cyan--text font-weight-bold">
                {{ formatPercentage3Decimal(item.management_percentage) }}%
              </span>
            </template>

            <!-- ร้อยละเลื่อนจริง -->
            <template v-slot:item.actual_percentage_final="{ item }">
              <span class="green--text font-weight-bold">
                {{ formatPercentage3Decimal(item.actual_percentage_final) }}%
              </span>
            </template>

            <!-- เงินที่ใช้เลื่อน (ทศนิยม 2) -->
            <template v-slot:item.actual_increase_amount="{ item }">
              <span class="font-weight-bold deep-orange--text text-right">
                {{ formatCurrency2Decimal(item.actual_increase_amount) }}
              </span>
            </template>

            <!-- ปัดเศษจริง -->
            <template v-slot:item.final_rounded="{ item }">
              <span class="font-weight-bold red--text text-right">
                {{ formatCurrency(item.final_rounded) }}
              </span>
            </template>

            <!-- เงินเดือนใหม่ -->
            <template v-slot:item.new_salary="{ item }">
              <span class="font-weight-bold blue--text text-right">
                {{ formatCurrency(item.new_salary) }}
              </span>
            </template>

            <!-- ปุ่มดำเนินการ -->
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    @click="viewDetails(item)"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>ดูรายละเอียด</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>

        <!-- สรุปผลรวมท้ายตาราง (แยกออก) -->
        <v-card class="mb-4 summary-card" outlined>
          <v-card-title class="blue white--text">
            <v-icon left>mdi-calculator-variant</v-icon>
            สรุปผลรวมทั้งสิ้น
          </v-card-title>
          <v-card-text class="pt-4">
            {{summaryTab}}
            <!-- Tab เลือกดูข้อมูล -->
            <v-tabs v-model="summaryTab" class="mb-4">
              <v-tab>📊 พื้นฐาน</v-tab>
              <v-tab>💰 ขั้นต้น (ยังไม่บริหาร)</v-tab>
              <v-tab>🎯 บริหารวงเงิน</v-tab>
              <v-tab>✅ ผลลัพธ์จริง</v-tab>
            </v-tabs>

            <v-tabs-items v-model="summaryTab">
              <!-- Tab 1: พื้นฐาน -->
              <v-tab-item>
                <v-row class="mt-2">
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📌 จำนวนพนักงาน</p>
                      <p class="summary-value">{{ totalPersonnel }}</p>
                      <p class="summary-unit">คน</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">💼 เงินเดือนรวม</p>
                      <p class="summary-value">{{ formatCurrency(totalSalary) }}</p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">🎯 คะแนนรวม</p>
                      <p class="summary-value">{{ formatPercentage(totalPerformanceScore) }}</p>
                      <p class="summary-unit">คะแนน</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">⚙️ ร้อยละเลื่อน</p>
                      <p class="summary-value">{{ compensationPercentage }}</p>
                      <p class="summary-unit">%</p>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 2: ขั้นต้น (ยังไม่บริหาร) -->
              <v-tab-item >
                <v-row class="mt-2">
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📊 สูงสุด 6% รวม</p>
                      <p class="summary-value purple--text">
                        {{ formatCurrency(totalMaxIncrease6Percent) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📈 ร้อยละรายบุคคล (รวม)</p>
                      <p class="summary-value info--text">
                        {{ formatPercentage3Decimal(totalIndividualPercentagePreManage) }}%
                      </p>
                      <p class="summary-unit">(ทศนิยม 3 หลัก)</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">💵 เงินเลื่อนขั้นต้น</p>
                      <p class="summary-value teal--text">
                        {{ formatCurrency2Decimal(totalInitialIncreasePreManage) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">🔹 ปัดเศษขั้นต้น</p>
                      <p class="summary-value indigo--text">
                        {{ formatCurrency(totalInitialRoundedPreManage) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                </v-row>

                <!-- เส้นแบ่ง -->
                <v-divider class="my-3"></v-divider>

                <!-- คำนวณวงเงินที่เหลือ -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">🎯 วงเงินค่าตอบแทนรวม</p>
                      <p class="info-value blue--text">
                        {{ formatCurrency(totalCompensationBudget) }} บาท
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">⚠️ ใช้ไปแล้ว (ปัดเศษต้น)</p>
                      <p class="info-value orange--text">
                        {{ formatCurrency(totalInitialRoundedPreManage) }} บาท
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">📊 วงเงินเหลือ (สำหรับบริหาร)</p>
                      <p class="info-value success--text font-weight-bold">
                        {{ formatCurrency(remainingBudgetAfterRounding) }} บาท
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">📈 ร้อยละบริหาร (เฉลี่ย)</p>
                      <p class="info-value cyan--text font-weight-bold">
                        {{ managementPercentageAverage.toFixed(3) }}%
                      </p>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 3: บริหารวงเงิน -->
              <v-tab-item >
                <v-row class="mt-2">
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">💱 ร้อยละบริหาร (รวม)</p>
                      <p class="summary-value cyan--text">
                        {{ formatPercentage3Decimal(totalManagementPercentage) }}%
                      </p>
                      <p class="summary-unit">(ทศนิยม 3 หลัก)</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📊 ร้อยละเลื่อนจริง (รวม)</p>
                      <p class="summary-value green--text font-weight-bold">
                        {{ formatPercentage3Decimal(totalActualPercentageFinal) }}%
                      </p>
                      <p class="summary-unit">(ทศนิยม 3 หลัก)</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">💰 เงินเลื่อนจริง (รวม)</p>
                      <p class="summary-value deep-orange--text">
                        {{ formatCurrency2Decimal(totalActualIncreaseAmount) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">🔹 ปัดเศษจริง (รวม)</p>
                      <p class="summary-value red--text font-weight-bold">
                        {{ formatCurrency(totalFinalRounded) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 4: ผลลัพธ์จริง -->
              <v-tab-item >
                <v-row class="mt-2">
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📋 เงินเดือนรวม (เดิม)</p>
                      <p class="summary-value">{{ formatCurrency(totalSalary) }}</p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">🎁 เงินเลื่อนรวม</p>
                      <p class="summary-value red--text font-weight-bold">
                        {{ formatCurrency(totalFinalRounded) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">💎 เงินเดือนใหม่ (รวม)</p>
                      <p class="summary-value blue--text font-weight-bold">
                        {{ formatCurrency(totalNewSalary) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-item">
                      <p class="summary-label">📈 ผลการใช้วงเงิน</p>
                      <p class="summary-value" :class="budgetStatus.class">
                        {{ budgetPercentage.toFixed(1) }}%
                      </p>
                      <p class="summary-unit">{{ budgetStatus.text }}</p>
                    </div>
                  </v-col>
                </v-row>

                <!-- สรุปผลลัพธ์ -->
                <v-divider class="my-3"></v-divider>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card class="pa-4" outlined :color="remainingBudget >= 0 ? 'success' : 'error'" text-color="white">
                      <p class="text-center subtitle-2 mb-2">✅ เงินคงเหลือ</p>
                      <p class="text-center display-1 font-weight-bold">
                        {{ formatCurrency(Math.abs(remainingBudget)) }}
                      </p>
                      <p class="text-center caption">
                        {{ remainingBudget >= 0 ? 'อยู่ในควบคุม' : 'เกินวงเงิน' }}
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card class="pa-4" outlined color="info" text-color="white">
                      <p class="text-center subtitle-2 mb-2">📊 อัตราเลื่อนเฉลี่ย</p>
                      <p class="text-center display-1 font-weight-bold">
                        {{ (totalFinalRounded / totalSalary * 100).toFixed(3) }}%
                      </p>
                      <p class="text-center caption">
                        ต่อทั้งหมด
                      </p>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- ปุ่มดำเนินการ -->
        <v-row class="mt-4">
          <v-col cols="12" class="text-center">
            <v-btn
              color="primary"
              large
              @click="calculateCompensation"
              :disabled="loading || !canCalculate"
              class="mr-2"
            >
              <v-icon left>mdi-calculator</v-icon>
              คำนวณค่าตอบแทน
            </v-btn>

            <v-btn
              color="success"
              large
              @click="approveCompensation"
              :disabled="!canApprove"
              class="mr-2"
            >
              <v-icon left>mdi-check-circle</v-icon>
              อนุมัติค่าตอบแทน
            </v-btn>

            <v-btn
              color="info"
              large
              @click="exportReport"
              :disabled="compensationData.length === 0 || !calculationComplete"
              class="mr-2"
            >
              <v-icon left>mdi-file-excel</v-icon>
              ส่งออก Excel
            </v-btn>

            <v-btn
              color="warning"
              large
              @click="resetAll"
              :disabled="loading"
            >
              <v-icon left>mdi-refresh</v-icon>
              เริ่มใหม่
            </v-btn>
          </v-col>
        </v-row>

        <!-- ส่วนประกอบการคำนวณ (ขึ้นขยาย) -->
        <v-expansion-panels class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header color="info" class="white--text">
              <v-icon left>mdi-information-outline</v-icon>
              <span class="subtitle-1 font-weight-bold">วิธีการคำนวณแบบละเอียด</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat class="pa-4 mt-3">
                <v-row>
                  <v-col cols="12">
                    <h3 class="mb-3">📐 ขั้นตอนการคำนวณค่าตอบแทน</h3>
                    <v-timeline align-top dense>
                      <v-timeline-item small>
                        <h4>ขั้นที่ 1: คำนวณสูงสุด 6% รายบุคคล</h4>
                        <p class="caption">
                          <code>max_increase = เงินเดือน × 0.06</code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 2: คำนวณร้อยละรายบุคคล (ยังไม่บริหาร)</h4>
                        <p class="caption">
                          <code>
                            individual_percentage = (6 × คะแนนรายบุคคล) / คะแนนรวม
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 3: คำนวณเงินเลื่อนขั้นต้น (ทศนิยม 2)</h4>
                        <p class="caption">
                          <code>
                            initial_increase = เงินเดือน × individual_percentage / 100
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 4: ปัดเศษขึ้นเป็นหลักสิบ</h4>
                        <p class="caption">
                          <code>
                            initial_rounded = ROUNDUP(initial_increase / 10) × 10
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 5: คำนวณร้อยละการบริหารวงเงิน</h4>
                        <p class="caption">
                          <code>
                            management_pct = (remaining_budget / total_salary) × 100
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 6: คำนวณร้อยละเลื่อนจริง</h4>
                        <p class="caption">
                          <code>
                            actual_percentage = individual_percentage + management_percentage
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 7: คำนวณเงินเลื่อนจริง (ทศนิยม 2)</h4>
                        <p class="caption">
                          <code>
                            actual_increase = เงินเดือน × actual_percentage / 100
                          </code>
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 8: ปัดเศษขึ้นเป็นหลักสิบ (ควบคุมวงเงิน)</h4>
                        <p class="caption">
                          <code>
                            final_rounded = ROUNDUP(actual_increase / 10) × 10
                          </code>
                        </p>
                        <p class="caption">
                          ⚠️ ต้องไม่เกินวงเงินรวม: ผลรวม final_rounded ≤ compensation_budget
                        </p>
                      </v-timeline-item>

                      <v-timeline-item small>
                        <h4>ขั้นที่ 9: คำนวณเงินเดือนใหม่</h4>
                        <p class="caption">
                          <code>new_salary = เงินเดือนเดิม + final_rounded</code>
                        </p>
                      </v-timeline-item>
                    </v-timeline>
                  </v-col>
                </v-row>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header color="warning" class="white--text">
              <v-icon left>mdi-check-circle</v-icon>
              <span class="subtitle-1 font-weight-bold">ตรวจสอบความถูกต้อง</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat class="pa-4 mt-3">
                <v-row>
                  <v-col cols="12">
                    <h3 class="mb-3">✓ เกณฑ์การตรวจสอบ</h3>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr class="blue--text">
                            <th>เกณฑ์</th>
                            <th>ค่าปัจจุบัน</th>
                            <th>สถานะ</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>ร้อยละเลื่อนรวม</td>
                            <td>{{ compensationPercentage }}%</td>
                            <td>
                              <v-chip
                                x-small
                                :color="compensationPercentage <= 4 ? 'success' : 'error'"
                                text-color="white"
                              >
                                {{ compensationPercentage <= 4 ? "✓ ผ่าน" : "✗ เกิน 4%" }}
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td>สูงสุดรายบุคคล</td>
                            <td>{{ maxAllowedPercentage }}%</td>
                            <td>
                              <v-chip
                                x-small
                                :color="maxAllowedPercentage <= 6 ? 'success' : 'error'"
                                text-color="white"
                              >
                                {{ maxAllowedPercentage <= 6 ? "✓ ผ่าน" : "✗ เกิน 6%" }}
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td>การใช้วงเงิน</td>
                            <td>{{ formatCurrency(totalCompensationDistributed) }} / {{ formatCurrency(totalCompensationBudget) }}</td>
                            <td>
                              <v-chip
                                x-small
                                :color="totalCompensationDistributed <= totalCompensationBudget ? 'success' : 'error'"
                                text-color="white"
                              >
                                {{ totalCompensationDistributed <= totalCompensationBudget ? "✓ ผ่าน" : "✗ เกินวงเงิน" }}
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td>จำนวนพนักงาน</td>
                            <td>{{ totalPersonnel }} คน</td>
                            <td>
                              <v-chip x-small color="info" text-color="white">
                                ✓ ข้อมูลพร้อม
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td>สถานะการคำนวณ</td>
                            <td>{{ calculationComplete ? "สมบูรณ์" : "รอคำนวณ" }}</td>
                            <td>
                              <v-chip
                                x-small
                                :color="calculationComplete ? 'success' : 'warning'"
                                text-color="white"
                              >
                                {{ calculationComplete ? "✓ คำนวณแล้ว" : "⏳ รอคำนวณ" }}
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </base-material-card>

      <!-- Dialog แสดงรายละเอียด -->
      <v-dialog v-model="detailDialog" max-width="900px" scrollable>
        <v-card>
          <v-card-title class="blue white--text d-flex justify-space-between">
            <span>📋 รายละเอียดการคำนวณค่าตอบแทน</span>
            <v-btn icon color="white" @click="detailDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4">
            <v-container v-if="selectedEmployee" fluid>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ชื่อ-นามสกุล"
                    :value="selectedEmployee.full_name"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ตำแหน่ง"
                    :value="selectedEmployee.name_postion"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-divider class="my-3"></v-divider>

                <v-col cols="12" class="subtitle-1 font-weight-bold">
                  📊 ข้อมูลพื้นฐาน
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเดือนปัจจุบัน (บาท)"
                    :value="formatCurrency(selectedEmployee.salary_s)"
                    readonly
                    outlined
                    dense
                    suffix="บาท"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="คะแนนการปฏิบัติงาน"
                    :value="selectedEmployee.performance_score"
                    readonly
                    outlined
                    dense
                    suffix="คะแนน"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="สูงสุดรายบุคคล 6% (บาท)"
                    :value="formatCurrency(selectedEmployee.max_increase_6_percent)"
                    readonly
                    outlined
                    dense
                    suffix="บาท"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เปอร์เซ็นต์การเลื่อน"
                    :value="compensationPercentage"
                    readonly
                    outlined
                    dense
                    suffix="%"
                  ></v-text-field>
                </v-col>

                <v-divider class="my-3"></v-divider>

                <v-col cols="12" class="subtitle-1 font-weight-bold">
                  🧮 ผลการคำนวณ (ยังไม่บริหารวงเงิน)
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละรายบุคคล"
                    :value="formatPercentage3Decimal(selectedEmployee.individual_percentage_pre_manage) + '%'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเลื่อนขั้นต้น"
                    :value="formatCurrency2Decimal(selectedEmployee.initial_increase_pre_manage) + ' บาท'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="ปัดเศษขั้นต้น"
                    :value="formatCurrency(selectedEmployee.initial_rounded_pre_manage) + ' บาท'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-divider class="my-3"></v-divider>

                <v-col cols="12" class="subtitle-1 font-weight-bold">
                  💰 ผลการบริหารวงเงิน
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละการบริหารวงเงิน"
                    :value="formatPercentage3Decimal(selectedEmployee.management_percentage) + '%'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละเลื่อนจริง"
                    :value="formatPercentage3Decimal(selectedEmployee.actual_percentage_final) + '%'"
                    readonly
                    outlined
                    dense
                    class="success--text"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเลื่อนจริง"
                    :value="formatCurrency2Decimal(selectedEmployee.actual_increase_amount) + ' บาท'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ปัดเศษจริง"
                    :value="formatCurrency(selectedEmployee.final_rounded) + ' บาท'"
                    readonly
                    outlined
                    dense
                    class="red--text"
                  ></v-text-field>
                </v-col>

                <v-divider class="my-3"></v-divider>

                <v-col cols="12" class="subtitle-1 font-weight-bold">
                  ✓ ผลลัพธ์สุดท้าย
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเดือนเดิม"
                    :value="formatCurrency(selectedEmployee.salary_s) + ' บาท'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเลื่อน"
                    :value="formatCurrency(selectedEmployee.final_rounded) + ' บาท'"
                    readonly
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    label="เงินเดือนใหม่"
                    :value="formatCurrency(selectedEmployee.new_salary) + ' บาท'"
                    readonly
                    outlined
                    dense
                    class="blue--text font-weight-bold"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="detailDialog = false">
              ปิด
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar แสดงผลลัพธ์ -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        elevation="24"
      >
        <div class="d-flex align-center">
          <v-icon left>{{ snackbar.icon }}</v-icon>
          {{ snackbar.text }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>

      <!-- Dialog ยืนยันการอนุมัติ -->
      <v-dialog v-model="approvalDialog" max-width="500px">
        <v-card>
          <v-card-title class="success white--text d-flex align-center">
            <v-icon left>mdi-check-circle</v-icon>
            ยืนยันการอนุมัติ
          </v-card-title>
          <v-card-text class="mt-4">
            <p class="subtitle-1">ท่านต้องการอนุมัติค่าตอบแทนหรือไม่?</p>
            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>จำนวนพนักงาน</v-list-item-title>
                  <v-list-item-subtitle>{{ totalPersonnel }} คน</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>วงเงินค่าตอบแทน</v-list-item-title>
                  <v-list-item-subtitle>{{ formatCurrency(totalCompensationBudget) }} บาท</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>จัดสรรแล้ว</v-list-item-title>
                  <v-list-item-subtitle>{{ formatCurrency(totalCompensationDistributed) }} บาท</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="gray" text @click="approvalDialog = false">
              ยกเลิก
            </v-btn>
            <v-btn color="success" text @click="confirmApproval">
              ยืนยันการอนุมัติ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Govem_bar from "../../../components/iven/govem_bar.vue";

export default {
  name: "CalculateCompensation",
  components: {
    Govem_bar
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      selectedYear: new Date().getFullYear() + 543,
      compensationPercentage: 4.0,
      maxAllowedPercentage: 6.0, // สูงสุดรายบุคคล
      roundingMethod: "ten", // วิธีปัดเศษ
      roundingMethods: [
        { text: "ปัดเศษขึ้นเป็นหลักสิบ", value: "ten" },
        { text: "ปัดเศษขึ้นเป็นร้อย", value: "hundred" }
      ],
      calculationComplete: false,
      detailDialog: false,
      approvalDialog: false,
      selectedEmployee: null,

      personnel_temporarys: [],
      compensationData: [],

      totalPersonnel: 0,
      totalSalary: 0,
      totalCompensationBudget: 0,
      totalCompensationDistributed: 0,
      remainingBudget: 0,

      years: [],
      selectedHeaders: [],
      visibleHeaders: [],

      allHeaders: [
        { text: "ลำดับที่", value: "order", width: "80px", align: "center", essential: true },
        { text: "ชื่อ-สกุล", value: "full_name", width: "180px", essential: true },
        { text: "เงินเดือน", value: "salary_s", width: "120px", align: "right", essential: true },
        { text: "คะแนน", value: "performance_score", width: "100px", align: "center", essential: true },
        { text: "สูงสุด 6%", value: "max_increase_6_percent", width: "120px", align: "right", essential: false },
        { text: "ร้อยละรายบุคคล", value: "individual_percentage_pre_manage", width: "130px", align: "center", essential: true },
        { text: "เลื่อนขั้นต้น", value: "initial_increase_pre_manage", width: "120px", align: "right", essential: true },
        { text: "ปัดเศษต้น", value: "initial_rounded_pre_manage", width: "110px", align: "right", essential: false },
        { text: "ร้อยละบริหาร", value: "management_percentage", width: "130px", align: "center", essential: false },
        { text: "ร้อยละจริง", value: "actual_percentage_final", width: "120px", align: "center", essential: true },
        { text: "เลื่อนจริง", value: "actual_increase_amount", width: "120px", align: "right", essential: true },
        { text: "ปัดเศษจริง", value: "final_rounded", width: "110px", align: "right", essential: true },
        { text: "เงินเดือนใหม่", value: "new_salary", width: "130px", align: "right", essential: false },
        { text: "ดู", value: "actions", width: "80px", align: "center", sortable: false, essential: false }
      ],

      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      },

      user: {},
      summaryTab: 0
    };
  },

  computed: {
    compensationHeaders() {
      return this.visibleHeaders;
    },

    canCalculate() {
      return (
        this.compensationData.length > 0 &&
        this.compensationData.every(item => item.performance_score > 0)
      );
    },

    canApprove() {
      return (
        this.calculationComplete &&
        this.totalCompensationDistributed > 0 &&
        this.totalCompensationDistributed <= this.totalCompensationBudget
      );
    },

    budgetStatus() {
      if (this.totalCompensationDistributed === 0) {
        return { text: "ยังไม่คำนวณ", color: "gray", class: "gray--text" };
      }
      if (this.totalCompensationDistributed <= this.totalCompensationBudget) {
        const percentage = (this.totalCompensationDistributed / this.totalCompensationBudget * 100).toFixed(1);
        if (percentage >= 95) {
          return { text: "ใกล้เต็ม", color: "warning", class: "warning--text" };
        }
        return { text: "อยู่ในควบคุม", color: "success", class: "success--text" };
      }
      return { text: "เกินวงเงิน", color: "error", class: "error--text" };
    },

    budgetPercentage() {
      if (this.totalCompensationBudget === 0) return 0;
      return (this.totalCompensationDistributed / this.totalCompensationBudget * 100);
    },

    remainingBudgetColor() {
      const remaining = this.remainingBudget;
      if (remaining < 0) return "red--text";
      if (remaining < this.totalCompensationBudget * 0.05) return "warning--text";
      return "success--text";
    },

    excessBudgetColor() {
      const excess = Math.max(0, this.totalCompensationDistributed - this.totalCompensationBudget);
      return excess > 0 ? "error--text" : "success--text";
    },

    // Computed properties สำหรับผลรวมคอลัมน์
    totalPerformanceScore() {
      return this.compensationData.reduce((sum, item) => sum + (item.performance_score || 0), 0);
    },

    totalMaxIncrease6Percent() {
      return this.compensationData.reduce((sum, item) => sum + (item.max_increase_6_percent || 0), 0);
    },

    totalIndividualPercentagePreManage() {
      return this.compensationData.reduce((sum, item) => sum + (item.individual_percentage_pre_manage || 0), 0);
    },

    totalInitialIncreasePreManage() {
      return this.compensationData.reduce((sum, item) => sum + (item.initial_increase_pre_manage || 0), 0);
    },

    totalInitialRoundedPreManage() {
      return this.compensationData.reduce((sum, item) => sum + (item.initial_rounded_pre_manage || 0), 0);
    },

    totalManagementPercentage() {
      return this.compensationData.reduce((sum, item) => sum + (item.management_percentage || 0), 0);
    },

    totalActualPercentageFinal() {
      return this.compensationData.reduce((sum, item) => sum + (item.actual_percentage_final || 0), 0);
    },

    totalActualIncreaseAmount() {
      return this.compensationData.reduce((sum, item) => sum + (item.actual_increase_amount || 0), 0);
    },

    totalFinalRounded() {
      return this.compensationData.reduce((sum, item) => sum + (item.final_rounded || 0), 0);
    },

    totalNewSalary() {
      return this.compensationData.reduce((sum, item) => sum + (item.new_salary || 0), 0);
    },

    // Computed for summary tab
    summaryTab: {
      get() {
        return this._summaryTab || 0;
      },
      set(val) {
        this._summaryTab = val;
      }
    },

    remainingBudgetAfterRounding() {
      return this.totalCompensationBudget - this.totalInitialRoundedPreManage;
    },

    managementPercentageAverage() {
      if (this.totalSalary === 0) return 0;
      return (this.remainingBudgetAfterRounding / this.totalSalary) * 100;
    }
  },

  watch: {
    compensationPercentage: {
      handler(newVal) {
        if (newVal && this.compensationData.length > 0) {
          this.recalculateBudget();
        }
      }
    },
    maxAllowedPercentage: {
      handler(newVal) {
        if (newVal && this.compensationData.length > 0) {
          this.recalculateBudget();
        }
      }
    }
  },

  async mounted() {
    await this.initializeComponent();
    this.initializeHeaders();
  },

  methods: {
    initializeHeaders() {
      this.selectedHeaders = this.allHeaders.filter(header => header.essential);
      this.visibleHeaders = [...this.selectedHeaders];
    },

    updateVisibleHeaders() {
      this.visibleHeaders = [...this.selectedHeaders];
    },

    resetHeaders() {
      this.initializeHeaders();
    },

    selectAllHeaders() {
      this.selectedHeaders = [...this.allHeaders];
      this.updateVisibleHeaders();
    },

    selectEssentialHeaders() {
      this.selectedHeaders = this.allHeaders.filter(header => header.essential);
      this.updateVisibleHeaders();
    },

    removeHeader(headerToRemove) {
      if (!headerToRemove.essential) {
        this.selectedHeaders = this.selectedHeaders.filter(
          header => header.value !== headerToRemove.value
        );
        this.updateVisibleHeaders();
      } else {
        this.showSnackbar("ไม่สามารถลบคอลัมน์ที่จำเป็นได้", "warning", "mdi-alert");
      }
    },

    async initializeComponent() {
      this.loading = true;
      try {
        let userSession = JSON.parse(localStorage.getItem("user")) || {};
        let resultUser = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_ID: userSession.user_ID
        });
        this.user = resultUser.data;

        this.generateYears();
        await this.loadEvaluationData();
        if (!this.compensationData.length) {
          await this.loadPersonnelData();
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    generateYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.years = [];
      for (let i = 0; i < 5; i++) {
        this.years.push(currentYear - i);
      }
    },

    async loadPersonnelData() {
      try {
        let result = await this.$http.post("personnel_temporary_govem.php", {
          ApiKey: this.ApiKey,
          user_code: this.user.user_code,
          user_status: "gov_em"
        });

        this.personnel_temporarys = result.data;
        this.prepareCompensationData();
        this.calculateTotalSalary();
      } catch (error) {
        this.showSnackbar("ไม่สามารถโหลดข้อมูลพนักงานได้", "error", "mdi-alert");
      }
    },

    prepareCompensationData() {
      this.compensationData = this.personnel_temporarys.map((employee, index) => ({
        order: index + 1,
        id_card: employee.id_card,
        frist_name: employee.frist_name,
        last_name: employee.last_name,
        full_name: `${employee.frist_name} ${employee.last_name}`,
        name_postion: employee.name_postion,
        salary_s: parseFloat(employee.salary_s) || 0,
        performance_score: 0,
        
        max_increase_6_percent: 0,
        individual_percentage_pre_manage: 0,
        initial_increase_pre_manage: 0,
        initial_rounded_pre_manage: 0,
        
        management_percentage: 0,
        actual_percentage_final: 0,
        actual_increase_amount: 0,
        
        final_rounded: 0,
        new_salary: parseFloat(employee.salary_s) || 0
      }));
    },

    validateScore(item) {
      // ตรวจสอบคะแนน 0-100
      if (item.performance_score > 100) item.performance_score = 100;
      if (item.performance_score < 0) item.performance_score = 0;
    },

    calculateTotalSalary() {
      this.totalPersonnel = this.compensationData.length;
      this.totalSalary = this.compensationData.reduce(
        (sum, item) => sum + item.salary_s,
        0
      );
      this.totalCompensationBudget = this.totalSalary * (this.compensationPercentage / 100);
    },

    recalculateBudget() {
      // ตรวจสอบขีดจำกัด
      if (this.compensationPercentage > 4) {
        this.compensationPercentage = 4;
        this.showSnackbar("ร้อยละเลื่อนไม่สามารถเกิน 4%", "warning", "mdi-alert");
      }
      if (this.maxAllowedPercentage > 6) {
        this.maxAllowedPercentage = 6;
        this.showSnackbar("สูงสุดรายบุคคลไม่สามารถเกิน 6%", "warning", "mdi-alert");
      }

      this.calculateTotalSalary();
      this.resetCalculation();
    },

    resetCalculation() {
      this.compensationData.forEach(item => {
        item.max_increase_6_percent = 0;
        item.individual_percentage_pre_manage = 0;
        item.initial_increase_pre_manage = 0;
        item.initial_rounded_pre_manage = 0;
        item.management_percentage = 0;
        item.actual_percentage_final = 0;
        item.actual_increase_amount = 0;
        item.final_rounded = 0;
        item.new_salary = item.salary_s;
      });
      this.totalCompensationDistributed = 0;
      this.remainingBudget = 0;
      this.calculationComplete = false;
    },

    calculateCompensation() {
      // ✓ ตรวจสอบข้อมูลเบื้องต้น
      if (!this.canCalculate) {
        this.showSnackbar(
          "กรุณากรอกคะแนนการปฏิบัติงานให้ครบทุกคน",
          "warning",
          "mdi-alert"
        );
        return;
      }

      try {
        // ✓ ขั้นตอนที่ 1: คำนวณสูงสุด 6% และร้อยละรายบุคคล (ยังไม่บริหารวงเงิน)
        const totalScore = this.compensationData.reduce(
          (sum, item) => sum + item.performance_score,
          0
        );

        if (totalScore === 0) {
          this.showSnackbar("ผลรวมคะแนนไม่สามารถเป็น 0 ได้", "error", "mdi-alert");
          return;
        }

        // ขั้นตอนที่ 1: คำนวณขั้นต้น
        this.compensationData.forEach(item => {
          // เลื่อนได้ไม่เกิน 6% = เงินเดือน × 0.06
          item.max_increase_6_percent = item.salary_s * (this.maxAllowedPercentage / 100);

          // ร้อยละที่ใช้เลื่อนรายบุคคล (ยังไม่บริหารวงเงิน)
          // = maxAllowed% × คะแนนส่วนบุคคล / คะแนนรวม
          item.individual_percentage_pre_manage = parseFloat(
            ((this.maxAllowedPercentage * item.performance_score) / totalScore).toFixed(3)
          );

          // ใช้เงินเลื่อนขั้นต้น = เงินเดือน × ร้อยละรายบุคคล / 100
          item.initial_increase_pre_manage = parseFloat(
            (item.salary_s * (item.individual_percentage_pre_manage / 100)).toFixed(2)
          );

          // ปัดเศษขั้นต้น
          item.initial_rounded_pre_manage = this.roundUp(
            item.initial_increase_pre_manage,
            this.roundingMethod
          );
        });

        // ✓ ขั้นตอนที่ 2: คำนวณวงเงินที่เหลือ
        const totalInitialRounded = this.compensationData.reduce(
          (sum, item) => sum + item.initial_rounded_pre_manage,
          0
        );
        const remainingBudgetAfterRounding = this.totalCompensationBudget - totalInitialRounded;

        // ขั้นตอนที่ 3: คำนวณร้อยละการบริหารวงเงิน (เฉลี่ยเท่ากันทุกราย)
        const managementPercentagePerPerson = parseFloat(
          ((remainingBudgetAfterRounding / this.totalSalary) * 100).toFixed(3)
        );

        // ขั้นตอนที่ 4: คำนวณค่าใหม่สำหรับแต่ละคน
        this.compensationData.forEach(item => {
          // ร้อยละการบริหารวงเงิน
          item.management_percentage = managementPercentagePerPerson;

          // ร้อยละเลื่อนจริง = ร้อยละรายบุคคล + ร้อยละบริหาร
          item.actual_percentage_final = parseFloat(
            (item.individual_percentage_pre_manage + item.management_percentage).toFixed(3)
          );

          // เงินเลื่อนจริง = เงินเดือน × ร้อยละเลื่อนจริง / 100
          item.actual_increase_amount = parseFloat(
            (item.salary_s * (item.actual_percentage_final / 100)).toFixed(2)
          );
        });

        // ✓ ขั้นตอนที่ 5: ปัดเศษขึ้นเป็นหลักสิบ (ควบคุมวงเงิน)
        this.roundToWithBudgetControl();

        // อัปเดตสถิติ
        this.updateStatistics();
        this.calculationComplete = true;

        this.showSnackbar("✓ คำนวณค่าตอบแทนเรียบร้อยแล้ว", "success", "mdi-check");
      } catch (error) {
        console.error(error);
        this.showSnackbar("เกิดข้อผิดพลาดในการคำนวณ: " + error.message, "error", "mdi-alert");
      }
    },

    roundUp(value, method) {
      if (method === "ten") {
        return Math.ceil(value / 10) * 10;
      } else if (method === "hundred") {
        return Math.ceil(value / 100) * 100;
      }
      return Math.ceil(value);
    },

    roundToWithBudgetControl() {
      const divisor = this.roundingMethod === "ten" ? 10 : 100;

      // ขั้นที่ 1: ปัดเศษขึ้นทุกรายการ
      this.compensationData.forEach(item => {
        item.final_rounded = Math.ceil(item.actual_increase_amount / divisor) * divisor;
        item.new_salary = item.salary_s + item.final_rounded;
      });

      // ขั้นที่ 2-3: ตรวจสอบและปรับปรุงเพื่อไม่เกินวงเงิน
      let totalRounded = this.totalFinalRounded;
      let iterations = 0;
      const maxIterations = 100;

      while (totalRounded > this.totalCompensationBudget && iterations < maxIterations) {
        // หารายที่มีจำนวนมากที่สุดและลดได้
        const maxItem = this.compensationData
          .filter(item => item.final_rounded >= divisor)
          .reduce((max, current) => 
            current.final_rounded > max.final_rounded ? current : max
          , { final_rounded: -1 });

        if (maxItem.final_rounded === -1) break;

        maxItem.final_rounded -= divisor;
        maxItem.new_salary = maxItem.salary_s + maxItem.final_rounded;
        totalRounded -= divisor;
        iterations++;
      }

      // ขั้นที่ 4: หากยังมีวงเงินเหลือ เพิ่มให้กับรายที่น้อยที่สุด
      iterations = 0;
      while (totalRounded <= this.totalCompensationBudget - divisor && iterations < maxIterations) {
        const minItem = this.compensationData.reduce((min, current) =>
          current.final_rounded < min.final_rounded ? current : min
        , this.compensationData[0]);

        minItem.final_rounded += divisor;
        minItem.new_salary = minItem.salary_s + minItem.final_rounded;
        totalRounded += divisor;
        iterations++;
      }

      this.updateStatistics();
    },

    updateStatistics() {
      this.totalCompensationDistributed = this.totalFinalRounded;
      this.remainingBudget = this.totalCompensationBudget - this.totalCompensationDistributed;
    },

    approveCompensation() {
      if (!this.canApprove) {
        this.showSnackbar(
          "ไม่สามารถอนุมัติได้ กรุณาตรวจสอบการคำนวณ",
          "error",
          "mdi-alert"
        );
        return;
      }
      this.approvalDialog = true;
    },

    async confirmApproval() {
      try {
        this.loading = true;
        this.approvalDialog = false;

        let result = await this.$http.post("compensation_approve.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code,
          compensation_data: this.compensationData,
          total_budget: this.totalCompensationBudget,
          total_distributed: this.totalCompensationDistributed,
          percentage: this.compensationPercentage
        });

        if (result.data.status) {
          this.showSnackbar("✓ อนุมัติค่าตอบแทนเรียบร้อยแล้ว", "success", "mdi-check");
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการอนุมัติ", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการอนุมัติ", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    async exportReport() {
      try {
        let result = await this.$http.post("compensation_export.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code,
          compensation_data: this.compensationData,
          summary: {
            totalPersonnel: this.totalPersonnel,
            totalSalary: this.totalSalary,
            totalCompensationBudget: this.totalCompensationBudget,
            totalCompensationDistributed: this.totalCompensationDistributed,
            percentage: this.compensationPercentage
          }
        });

        if (result.data.file_url) {
          window.open(result.data.file_url, "_blank");
          this.showSnackbar("✓ ส่งออกรายงาน Excel เรียบร้อยแล้ว", "success", "mdi-check");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออกรายงาน", "error", "mdi-alert");
      }
    },

    async loadEvaluationData() {
      try {
        let result = await this.$http.post("compensation_history.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code
        });

        if (result.data.length > 0) {
          this.compensationData = result.data;
          this.calculationComplete = true;
          this.updateStatistics();
        }
      } catch (error) {
        console.log("ไม่มีข้อมูลการประเมินในปีนี้");
      }
    },

    viewDetails(item) {
      this.selectedEmployee = item;
      this.detailDialog = true;
    },

    getPercentageColor(percentage) {
      if (percentage >= 5) return "success--text";
      if (percentage >= 3) return "info--text";
      if (percentage > 0) return "warning--text";
      return "error--text";
    },

    formatCurrency(value) {
      if (isNaN(value) || value === null || value === undefined) return "0";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },

    formatCurrency2Decimal(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatPercentage(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toFixed(2);
    },

    formatPercentage3Decimal(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.000";
      return Number(value).toFixed(3);
    },

    resetAll() {
      if (confirm("ต้องการเริ่มต้นใหม่? ข้อมูลการคำนวณปัจจุบันจะหายไป")) {
        this.calculationComplete = false;
        this.resetCalculation();
        this.loadPersonnelData();
        this.showSnackbar("เริ่มต้นใหม่เรียบร้อย", "info", "mdi-information");
      }
    },

    showSnackbar(text, color = "success", icon = "mdi-check") {
      this.snackbar = {
        show: true,
        text,
        color,
        icon,
        timeout: 3000
      };
    }
  }
};
</script>

<style scoped>
/* ทั่วไป */
.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-card-text h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.v-card-text p {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.headline {
  font-weight: 500;
}

/* Input fields */
.score-input >>> .v-input__control {
  min-height: 40px;
}

/* สีสำหรับข้อมูลแต่ละอัตรา */
.success--text {
  color: #4caf50 !important;
  font-weight: 500;
}

.info--text {
  color: #2196f3 !important;
  font-weight: 500;
}

.warning--text {
  color: #ff9800 !important;
  font-weight: 500;
}

.error--text {
  color: #f44336 !important;
  font-weight: 500;
}

.primary--text {
  color: #1976d2 !important;
}

.orange--text {
  color: #ff5722 !important;
}

.green--text {
  color: #388e3c !important;
}

.red--text {
  color: #d32f2f !important;
}

.purple--text {
  color: #7b1fa2 !important;
}

.blue--text {
  color: #1565c0 !important;
}

.teal--text {
  color: #00796b !important;
}

.indigo--text {
  color: #3f51b5 !important;
}

.cyan--text {
  color: #00bcd4 !important;
}

.deep-orange--text {
  color: #ff5722 !important;
}

.gray--text {
  color: #9e9e9e !important;
}

/* Style สำหรับแถวผลรวม */
.summary-row {
  background-color: #f5f5f5 !important;
  border-top: 2px solid #2196f3 !important;
  border-bottom: 2px solid #2196f3 !important;
}

.summary-row td {
  font-weight: bold !important;
  color: #1565c0 !important;
  padding: 12px 4px !important;
}

.v-data-table >>> .v-data-footer {
  border-top: 2px solid #ddd;
}

/* ความสูงตาราง */
.v-data-table >>> .v-data-table__wrapper {
  max-height: 600px;
  overflow-y: auto;
}

/* Chip group */
.v-chip-group {
  max-height: 100px;
  overflow-y: auto;
}

/* การ์ด */
.v-card.outlined {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.text-subtitle-2 {
  font-weight: 600;
  color: #424242;
}

/* ปุ่มเครื่องมือ */
.v-btn.v-size--small {
  font-size: 0.75rem;
}

/* Chips ใน selection */
.v-select__selections .v-chip {
  margin: 2px 4px 2px 0;
}

/* ปรับความสูง select */
.v-input--dense .v-input__control {
  min-height: 40px;
}

/* Timeline */
.v-timeline-item >>> .v-timeline-item__content {
  padding-top: 10px;
}

.v-timeline >>> .v-timeline-item__dot {
  background-color: #2196f3;
}

/* Display sizes */
.display-1 {
  font-size: 3rem;
  font-weight: 300;
}

/* Text alignment */
.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

/* Timeline */
.v-timeline-item >>> .v-timeline-item__content {
  padding-top: 10px;
}

.v-timeline >>> .v-timeline-item__dot {
  background-color: #2196f3;
}

/* Display sizes */
.display-1 {
  font-size: 3rem;
  font-weight: 300;
}

/* Text alignment */
.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

/* ===== Summary Section Styles ===== */
.summary-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.summary-card >>> .v-card__title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border-radius: 12px 12px 0 0 !important;
  padding: 20px !important;
}

.summary-item {
  background: white;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  margin: 8px 0;
  color: #1565c0;
}

.summary-unit {
  font-size: 11px;
  color: #999;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.info-card {
  background: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
  border-left: 4px solid #667eea !important;
  transition: all 0.3s ease;
}

.info-card:hover {
  background: #f0f2f5 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.info-title {
  font-weight: 600;
  font-size: 13px;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.info-value {
  font-size: 28px;
  font-weight: bold;
  margin: 12px 0 0 0;
}

/* Tab styles */
.v-tabs >>> .v-tabs-bar {
  background: linear-gradient(90deg, #f5f7fa 0%, #c3cfe2 100%) !important;
  border-bottom: 2px solid #667eea !important;
}

.v-tabs >>> .v-tab--active {
  color: #667eea !important;
  font-weight: 600 !important;
}

.v-tabs >>> .v-tab {
  font-weight: 500;
  font-size: 14px;
}

.v-tabs-items {
  background: transparent !important;
}

/* Color classes for summary values */
.purple--text {
  color: #7b1fa2 !important;
  font-weight: 600;
}

.info--text {
  color: #2196f3 !important;
  font-weight: 600;
}

.teal--text {
  color: #00796b !important;
  font-weight: 600;
}

.indigo--text {
  color: #3f51b5 !important;
  font-weight: 600;
}

.cyan--text {
  color: #00bcd4 !important;
  font-weight: 600;
}

.deep-orange--text {
  color: #ff5722 !important;
  font-weight: 600;
}

/* Responsive for summary */
@media (max-width: 600px) {
  .summary-item {
    padding: 12px;
    margin-bottom: 12px;
  }

  .summary-value {
    font-size: 18px;
  }

  .display-1 {
    font-size: 1.75rem;
  }

  .info-value {
    font-size: 20px;
  }
}
</style>